import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME } from "../../configs/config";

const HowToUse: React.FC = () => {
  return (
    <div className="how-to-use-container">
      <div className="how-to-use-content">
        <h1 className="how-to-use-title">How to Use {APP_NAME}</h1>

        <div className="how-to-use-intro">
          <p>
            Welcome to {APP_NAME}! Here is your quick guide to start using the
            platform and trade on the markets.
          </p>
        </div>

        <div className="how-to-use-section">
          <h2>Getting Started</h2>

          <div className="how-to-use-step">
            <h3>Step 1: Connect Your Wallet</h3>
            <p>
              Click <strong>Connect Wallet</strong> in the header. You need an
              XPR wallet.
            </p>
            <ul>
              <li>Your wallet session stays connected across refreshes.</li>
              <li>
                All buys, sells, proposal submissions, votes, and claims require
                wallet confirmation.
              </li>
              <li>You can disconnect at any time from the wallet menu.</li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 2: Browse Markets</h3>
            <p>
              The home page shows live, expired, resolved, and watchlisted
              markets. Each card shows the market question, category, expiry,
              participant count, market capital, and current odds or
              probabilities.
            </p>
            <ul>
              <li>
                <strong>Live:</strong> Open for trading.
              </li>
              <li>
                <strong>Expired:</strong> Trading has ended and the market is
                waiting for resolution.
              </li>
              <li>
                <strong>Resolved:</strong> A final outcome has been set.
              </li>
              <li>
                <strong>Illiquid:</strong> The market expired with no trades or
                one-sided trading.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 3: Understand LMSR Pricing</h3>
            <p>
              Current markets use an{" "}
              <strong>AMM (Automated Market Maker)</strong>. Prices move
              continuously in real-time as traders buy and sell, and the
              displayed price acts like the market&apos;s current probability.
            </p>
            <ul>
              <li>Binary markets show one buy action per listed outcome.</li>
              <li>
                Multi-outcome markets let you buy <strong>YES</strong> or{" "}
                <strong>NO</strong> for each outcome.
              </li>
              <li>
                The prices and odds are updated in real-time as the market
                changes.
              </li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-section">
          <h2>Trading</h2>

          <div className="how-to-use-step">
            <h3>Step 4: Buy Shares</h3>
            <p>
              Trading on {APP_NAME} is very simple. Open a market → Choose an
              outcome and Click the buy action.
            </p>
            <ul>
              <li>
                A confirmation Modal will show Estimated shares, Average price,
                Fee, and updated odds.
              </li>
              <li>Current markets charge a 1% trading fee.</li>
              <li>
                <strong>Note:</strong> Trading gets disabled once the market
                reaches expiry.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 5: Sell Shares Before Expiry</h3>
            <p>
              If you already hold some shares for a market, use the{" "}
              <strong>Sell</strong> button to exit your positions. Selling is
              based on the shares you hold, and you will be shown an estimated
              payout before you confirm your <strong>Sell</strong> trade.
            </p>
            <ul>
              <li>You can sell Fractional Shares.</li>
              <li>The payout changes realtime with market conditions.</li>
              <li>
                You cannot buy or sell after the market expiry or once the
                market is resolved.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 6: Use the Market Detail Page</h3>
            <p>
              Each market page includes the Resolution criteria, Current
              positions, User's Opinions in Comment Section and other vital
              aspects of a Market.
            </p>
            <ul>
              <li>
                <strong>Trade:</strong> Buy and sell shares, view your position,
                and review the market rules.
              </li>
              <li>
                <strong>Comments:</strong> Discuss the market while trading is
                still active.
              </li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-section">
          <h2>Resolution Flow</h2>

          <div className="how-to-use-step">
            <h3>Step 7: What Happens at Expiry</h3>
            <p>
              Once the expiry time passes, trading stops and the market moves
              into the resolution flow. You can follow this on the{" "}
              <Link to="/resolution">Market Resolution</Link> page.
            </p>
            <ul>
              <li>
                <strong>Open:</strong> Expired and waiting for a proposal.
              </li>
              <li>
                <strong>Voting:</strong> A proposal exists and eligible voters
                can vote.
              </li>
              <li>
                <strong>Stalled:</strong> Voting ended without enough votes to
                pass.
              </li>
              <li>
                <strong>Closed:</strong> Resolution is finalized and claims can
                begin.
              </li>
              <li>
                <strong>Illiquid:</strong> No valid proposal flow because the
                market had no trades or only one-sided trading.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 8: Submit a Resolution Proposal</h3>
            <p>
              For expired markets in the <strong>Open</strong> tab, any
              connected user can open the market and submit a resolution
              proposal.
            </p>
            <ul>
              <li>
                Review the market description and resolution criteria first.
              </li>
              <li>Submitting the proposal starts the voting phase.</li>
              <li>
                The eligible voter snapshot is built realtime from the onchain
                data for that proposal.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 9: Voting and Finalization</h3>
            <p>
              During the <strong>Voting</strong> stage, only the eligible XPRED
              voter set can cast a vote on the proposal. The market detail page
              shows the proposal, vote totals, and whether your account is
              eligible.
            </p>
            <ul>
              <li>
                Platform always encourages you to cast your vote and drive the
                platform forward with community engagement.
              </li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 10: Discussion and Evidence</h3>
            <p>
              Resolution discussions are open during <strong>Voting</strong> and{" "}
              stage. Users can comment and share their Opinions to help
              resolvers decide the outcome.
            </p>
            <ul>
              <li>
                Once a market is closed, the discussion stays visible but new
                comments and reactions are locked.
              </li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-section">
          <h2>Portfolio and Claims</h2>

          <div className="how-to-use-step">
            <h3>Step 12: Track Positions in Portfolio</h3>
            <p>
              The Portfolio page groups your holdings by market and shows
              whether each position is live, resolved, or refundable.
            </p>
            <ul>
              <li>Resolved markets show the winning outcome.</li>
            </ul>
          </div>

          <div className="how-to-use-step">
            <h3>Step 13: Claim Winnings or Refunds</h3>
            <p>
              Claims are handled from the Portfolio page after the market is
              closed or marked refundable.
            </p>
            <ul>
              <li>
                <strong>Claim Winnings</strong> will let you claim your winnings
                from a market.
              </li>
              <li>
                <strong>Claim Refund</strong> helps you get your tokens back
                from a illiquid market.{" "}
              </li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-section">
          <h2>Tips</h2>

          <div className="how-to-use-tips">
            <ul>
              <li>
                Read the market&apos;s resolution criteria before trading.
              </li>
              
              <li>Remember that expired markets stop trading immediately.</li>
              <li>
                Check the Resolution page after expiry if you want to follow the
                market through proposal, voting, and finalization.
              </li>
              <li>
                Use the Portfolio page to claim winnings or refunds as soon as
                they become available.
              </li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-section">
          <h2>Need Help?</h2>

          <div className="how-to-use-help">
            <p>If something does not work as expected:</p>
            <ul>
              <li>
                Confirm your wallet is connected before trading, voting, or
                claiming.
              </li>
              <li>
                Hover tooltip icons in the UI for field-level explanations.
              </li>
              <li>Use the footer links for community or support channels.</li>
            </ul>
          </div>
        </div>

        <div className="how-to-use-footer">
          <p>
            Ready to start? <Link to="/">Browse Markets</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;